import {
  useApi,
  discoveryApiRef,
  fetchApiRef,
} from '@backstage/core-plugin-api';
import { useAsync } from 'react-use';

export type LatestRelease = {
  tagName: string;
  name?: string;
  releasedAt: string;
  webUrl?: string;
};

type LatestReleases = Record<string, LatestRelease>;

// The catalog table renders this hook once per row; a module-level cache
// makes that a single request per page load. The short TTL only matters
// for long-lived tabs, since ingestion runs on a 30-minute schedule anyway.
let cache: { at: number; value: Promise<LatestReleases> } | undefined;
const CACHE_TTL_MS = 5 * 60 * 1000;

/**
 * The most recent release per entity, keyed by entity ref. Undefined while
 * loading or when the request failed (callers render an empty state).
 */
export function useLatestReleases(): LatestReleases | undefined {
  const discoveryApi = useApi(discoveryApiRef);
  const { fetch } = useApi(fetchApiRef);

  const { value } = useAsync(async (): Promise<LatestReleases> => {
    if (!cache || Date.now() - cache.at > CACHE_TTL_MS) {
      const entry = {
        at: Date.now(),
        value: (async () => {
          const baseUrl = await discoveryApi.getBaseUrl('releases');
          const response = await fetch(`${baseUrl}/latest`);
          if (!response.ok) {
            throw new Error(
              `Request failed with ${response.status} ${response.statusText}`,
            );
          }
          return (await response.json()).latest ?? {};
        })(),
      };
      cache = entry;
      // Drop a failed fetch from the cache so the next mount retries.
      entry.value.catch(() => {
        if (cache === entry) {
          cache = undefined;
        }
      });
    }
    return cache.value;
  }, []);

  return value;
}
